import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2, BellRing, Phone, Clock, AlertTriangle, CheckCircle2, CalendarClock, RotateCcw, X,
} from "lucide-react";

const TABS = [
  { key: "", label: "All" },
  { key: "due_today", label: "Due today" },
  { key: "overdue", label: "Overdue" },
  { key: "due_soon", label: "Due soon" },
  { key: "scheduled", label: "Scheduled" },
  { key: "completed", label: "Completed" },
];
const STATUS_STYLE = {
  overdue: ["rgba(156,30,34,0.1)", "#9C1E22"], due_today: ["rgba(183,121,31,0.14)", "#B7791F"],
  due_soon: ["rgba(37,99,235,0.1)", "#2563EB"], scheduled: ["rgba(28,67,88,0.08)", "#1C4358"],
  completed: ["rgba(47,125,90,0.12)", "#2F7D5A"],
};
const pretty = (s) => String(s == null ? "" : s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function CrmFollowUps() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(sp.get("status") || "");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/crm/follow-ups", { params: { status: tab || undefined } }); setData(data); }
    catch { toast.error("Could not load follow-ups."); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action, body) => {
    try { await api.post(`/crm/follow-ups/${id}/${action}`, body || {}); toast.success("Updated."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const counts = data?.counts || {};
  const names = data?.user_names || {};

  return (
    <div className="max-w-4xl mx-auto pb-12" data-testid="crm-followups">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2"><BellRing size={22} className="text-[#1C4358]" /><h1 className="text-2xl font-display font-bold text-[#1C4358]">Follow-Ups</h1></div>
        <Button variant="outline" size="sm" onClick={load} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
        {[["Overdue", counts.overdue, "#9C1E22"], ["Due today", counts.due_today, "#B7791F"], ["Due soon", counts.due_soon, "#2563EB"], ["Scheduled", counts.scheduled, "#1C4358"], ["Missed", counts.missed, "#9C1E22"]].map(([l, v, c], i) => (
          <div key={i} className="rounded-lg border border-[#E6EDF2] bg-white p-3"><div className="text-2xl font-display font-bold" style={{ color: c }}>{v || 0}</div><div className="text-[11px] text-[#4B5563]">{l}</div></div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`fu-tab-${t.key || "all"}`} onClick={() => setTab(t.key)}
            className={`rounded-full px-3.5 h-9 text-[13px] font-semibold transition-colors ${tab === t.key ? "bg-[#1C4358] text-white" : "bg-white border border-[#E6EDF2] text-[#1C4358]"}`}>{t.label}</button>
        ))}
      </div>

      {loading && !data ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1C4358]" size={28} /></div> :
        (data.items || []).length === 0 ? <div className="text-center py-16 text-[#4B5563]"><CheckCircle2 className="mx-auto mb-2 text-[#2F7D5A]" /> Nothing here — you're all caught up.</div> : (
          <div className="space-y-2">
            {data.items.map((f) => {
              const [bg, c] = STATUS_STYLE[f.computed_status] || ["rgba(75,85,99,0.1)", "#4B5563"];
              return (
                <div key={f.id} data-testid={`fu-row-${f.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#E6EDF2] bg-white p-3 flex-wrap">
                  <div className="min-w-0">
                    <button onClick={() => navigate(`/crm/leads/${f.lead_id}`)} className="font-semibold text-[#1C4358] text-[15px] hover:underline">{f.lead_name || "Lead"}</button>
                    <div className="text-[12px] text-[#4B5563] flex items-center gap-2 flex-wrap mt-0.5">
                      <span className="flex items-center gap-1"><Clock size={11} /> {f.due_date}{f.will_call_back ? " · call back" : ""}</span>
                      {f.service_lane && <span>{pretty(f.service_lane)}</span>}
                      {names[f.assignee_id] && <span>· {names[f.assignee_id]}</span>}
                      {f.note && <span className="text-[#9AA5B1]">· {f.note}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded flex items-center gap-1" style={{ backgroundColor: bg, color: c }}>
                      {f.computed_status === "overdue" && <AlertTriangle size={11} />}{pretty(f.computed_status)}
                    </span>
                    {f.status === "scheduled" && <>
                      <Button size="sm" data-testid={`fu-complete-${f.id}`} onClick={() => act(f.id, "complete")} className="h-8 bg-[#2F7D5A] hover:bg-[#276848] text-white gap-1 text-[12px]"><CheckCircle2 size={13} /> Done</Button>
                      <Button size="sm" variant="outline" data-testid={`fu-reschedule-${f.id}`} onClick={() => act(f.id, "reschedule", { business_days: 3 })} className="h-8 border-[#D6E0E7] text-[#1C4358] gap-1 text-[12px]"><CalendarClock size={13} /> +3d</Button>
                      <Button size="sm" variant="ghost" data-testid={`fu-cancel-${f.id}`} onClick={() => act(f.id, "cancel")} className="h-8 text-[#9C1E22] gap-1 text-[12px]"><X size={13} /></Button>
                    </>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
